import { isValidPhoneNumber } from 'libphonenumber-js';
import * as Yup from 'yup';
import { ReferenceType } from '../enums/ReferenceType';
import { checkValidDate, stringToDate } from '../helpers/dateHelper';

const emailSchema = Yup.string()
  .when('phoneNumber', (phoneNumber: string, schema: Yup.MixedSchema) => (
    phoneNumber ? schema : schema.required('Required')
  ))
  .max(255, 'Invalid email address')
  .email('Invalid email address');

const phoneNumberSchema = Yup.string()
  .nullable()
  .test('phoneNumber', 'Invalid phone number', value => !value || isValidPhoneNumber(value));

const requiredPhoneNumberSchema = Yup.string()
  .required('Required')
  .test('phoneNumber', 'Invalid phone number', value => !value || isValidPhoneNumber(value));

const requiredTextField = Yup.string().required('Required');

const workStartDateSchema = Yup.string()
  .required('Required')
  .min(6, 'Please provide a valid start date')
  .test('workStartDate', 'Please provide a valid start date', value => checkValidDate(value as string));

const workEndDateSchema = Yup.string()
  .notRequired()
  .nullable()
  .min(6, 'Please provide a valid start date')
  .test('workEndDate', 'Please provide a valid start date', val => !val || checkValidDate(val as string))
  .when('workStartDate',
    (workStartDate: string, schema: Yup.StringSchema) => workStartDate
      && schema.test('workEndDate', 'End date must be after start date', value => !value
      || stringToDate(value) >= stringToDate(workStartDate)));

export const reqBuilderStep2ValidationSchema = Yup.object().shape({
  email: emailSchema,
  phoneNumber: phoneNumberSchema,
  lastName: requiredTextField,
  firstName: requiredTextField
});

export const editContactValidationSchema = Yup.object().shape({
  email: emailSchema.when('phoneNumber', (phoneNumber: string, schema: Yup.MixedSchema) => (
    phoneNumber ? schema : schema.required('Email is required')
  )).nullable(),
  phoneNumber: phoneNumberSchema,
  firstName: Yup.string().required('First name is required'),
  lastName: Yup.string().required('Last name is required')
});

export const personalPhoneNumberValidationSchema = Yup.object().shape({ phoneNumber: phoneNumberSchema });

export const requiredPhoneNumberValidationSchema = Yup.object().shape({ phoneNumber: requiredPhoneNumberSchema });

export const nameValidationSchema = Yup.object().shape({
  firstName: requiredTextField,
  lastName: requiredTextField
});

export const contactInfoValidationSchema = Yup.object().shape({
  email: emailSchema,
  phoneNumber: phoneNumberSchema
});

export const referenceCompanyValidationSchema = () => Yup.object().shape({
  workInCompany: requiredTextField.trim(),
  workStartDate: workStartDateSchema,
  workEndDate: workEndDateSchema
});

export const editReferenceValidationSchema = () => Yup.object().shape({
  firstName: requiredTextField,
  lastName: requiredTextField,
  phoneNumber: phoneNumberSchema,
  email: emailSchema,
  workInCompany: requiredTextField.trim(),
  workStartDate: workStartDateSchema,
  workEndDate: workEndDateSchema
});

export const confirmContactInfoValidationSchema = Yup.object().shape({
  firstName: requiredTextField,
  lastName: requiredTextField,
  phoneNumber: requiredPhoneNumberSchema,
  email: emailSchema.required().nullable()
});

export const confirmRelationshipValidationSchema = Yup.object().shape({
  workInCompany: requiredTextField,
  workStartDate: workStartDateSchema,
  workEndDate: workEndDateSchema,
  title: requiredTextField.nullable(),
  type: requiredTextField,
  otherType: Yup.string().when('type', (type: ReferenceType, schema: Yup.MixedSchema) => (
    type === ReferenceType.Other ? schema.required('Required') : schema
  ))
});

export const createUserValidationSchema = Yup.object().shape({
  firstName: requiredTextField,
  lastName: requiredTextField,
  email: emailSchema,
  role: requiredTextField,
  team: Yup.object().required()
});

export const updateProfileValidationSchema = Yup.object().shape({
  firstName: requiredTextField,
  lastName: requiredTextField,
  team: Yup.object().required(),
  phoneNumber: phoneNumberSchema.nullable(),
  initials: Yup.string().required('Required').max(3, 'initialsLengthError')
});

export const validateEmail = (emailVal: string) => Yup.string()
  .required()
  .max(255, 'Invalid email address')
  .email('Invalid email address')
  .isValidSync(emailVal);
